import { useContext, useEffect, useState } from 'react';
import { getUser as getUserApi } from '../api/user';
import type { User } from '../context/User';
import { OverlayConfigContext } from '../context';
import type { OverlayConfigOptions } from '../context/Overlay';
import logger from '../logger';

/**
 * Applies the provided configuration to the mounted modal on mount.
 * (Ported from eb-ui src/hooks/index.ts.)
 */
export const useOverlayConfig = (config: Partial<OverlayConfigOptions>) => {
    const { overlayConfig, setOverlayConfig } =
        useContext(OverlayConfigContext);

    useEffect(() => {
        const newConfig = { ...overlayConfig, ...config } as OverlayConfigOptions;
        setOverlayConfig && setOverlayConfig(newConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { overlayConfig };
};

/**
 * Scrolls the element with the given className to the top of the viewport.
 */
export const useScrollTop = (className: string) => {
    useEffect(() => {
        const element = document.getElementsByClassName(className)[0];

        if (element) {
            if (element.scrollTo) {
                element.scrollTo(0, 0);
            } else {
                element.scroll(0, 0);
            }
        }
    }, [className]);
};

/**
 * If authenticated, fetches the current user (via the app's BFF) on mount and
 * returns the user object (used to prefill name/email).
 */
export const useUserData = (
    isAuthenticated?: boolean,
    userPath?: string,
): User | null => {
    const [userData, setUserData] = useState<User | null>(null);

    useEffect(() => {
        if (isAuthenticated) {
            const getUser = async () => {
                try {
                    const data = await getUserApi(userPath);
                    setUserData(data);
                } catch (error) {
                    logger.error('Error getting user object:', {
                        error:
                            error instanceof Error ? error.message : String(error),
                    });
                }
            };

            getUser();
        }
    }, [isAuthenticated, userPath]);

    return userData;
};
